import type {
	FieldFilter,
	FieldFilterOperator,
	Filter,
	FilterRelationalQuantifier,
	LogicalFilter,
	LogicalFilterAND,
	LogicalFilterOR,
	UnaryFilterNot,
} from '#layers/data-model/types/filters';
import type { AnyItem } from '#layers/data-model/types/schema/item';
import { isObject } from '@vueuse/core';
import { compareNumericOrNull, compareTemporalMoment, get, isDeepEqual, isNil } from '#layers/base/public';
import {
	FILTER_OPERATORS,
	FILTER_RELATIONAL_QUANTIFIERS,
	FilterOperator,
	LogicalOperator,
	UnaryFilterOperator,
} from '#layers/data-model/types/filters';

/**
 * Compares an item field value against a filter operand, or null when they aren't
 * mutually comparable (so callers fall back to `isDeepEqual`/no-match). Numeric and
 * Temporal comparison is shared with the sort path via `#layers/base/public`.
 *
 * Item field values (parsed on ingest, see workspace-schema `fieldParsers`) and operands
 * (built by the filter UI) are always canonical Temporal instances — never strings — by the
 * time they reach here. A stray string is a boundary leak to fix upstream, and falls through
 * to the generic string comparison below.
 */
function compareFilterComparable(a: unknown, b: unknown): number | null {
	const numericComparison = compareNumericOrNull(a, b);

	if (numericComparison !== null) {
		return numericComparison;
	}

	const temporalComparison = compareTemporalMoment(a, b);

	if (temporalComparison !== null) {
		return temporalComparison;
	}

	if (typeof a !== 'string' || typeof b !== 'string') return null;

	if (a === b) return 0;
	return a < b ? -1 : 1;
}

function matchesComparableValue(itemValue: unknown, filterValue: unknown): boolean {
	const comparison = compareFilterComparable(itemValue, filterValue);

	if (comparison !== null) {
		return comparison === 0;
	}

	return isDeepEqual(filterValue, itemValue);
}

/** Runs a string predicate only when both operands are strings; non-strings never match. */
function matchesStringPredicate(itemValue: unknown, filterValue: unknown, predicate: (item: string, operand: string) => boolean): boolean {
	return typeof itemValue === 'string' && typeof filterValue === 'string' && predicate(itemValue, filterValue);
}

/** Whether itemValue falls within [lower, upper], or null when either bound isn't comparable. */
function isWithinRange(itemValue: unknown, lower: unknown, upper: unknown): boolean | null {
	const lowerComparison = compareFilterComparable(itemValue, lower);
	const upperComparison = compareFilterComparable(itemValue, upper);

	if (lowerComparison === null || upperComparison === null) return null;

	return lowerComparison >= 0 && upperComparison <= 0;
}

type ItemShape = AnyItem;

export function isLogicalFilterAnd<TItem extends ItemShape>(filter?: Filter<TItem> | null): filter is LogicalFilterAND<TItem> {
	return !!filter && LogicalOperator.AND in filter && Array.isArray(filter._and);
}

export function isLogicalFilterOr<TItem extends ItemShape>(filter?: Filter<TItem> | null): filter is LogicalFilterOR<TItem> {
	return !!filter && LogicalOperator.OR in filter && Array.isArray(filter._or);
}

export function isUnaryFilterNot<TItem extends ItemShape>(filter?: Filter<TItem> | null): filter is UnaryFilterNot<TItem> {
	return !!filter && UnaryFilterOperator.NOT in filter;
}

export function isLogicalFilter<TItem extends ItemShape>(filter?: Filter<TItem> | null): filter is LogicalFilter<TItem> {
	return isLogicalFilterAnd(filter) || isLogicalFilterOr(filter);
}

export function isFieldFilter<TItem extends ItemShape>(filter?: Filter | null): filter is FieldFilter<TItem> {
	return !isLogicalFilter(filter) && !isUnaryFilterNot(filter);
}

export function isEmptyFilter(filter?: Filter | null) {
	if (isNil(filter)) {
		return true;
	}
	else if (isLogicalFilterAnd(filter)) {
		return filter._and.length === 0;
	}
	else if (isLogicalFilterOr(filter)) {
		return filter._or.length === 0;
	}
	else if (isUnaryFilterNot(filter)) {
		return isEmptyFilter(filter._not);
	}
	else {
		return Object.keys(filter).length === 0;
	}
}

export function isNonEmptyFilter(filter?: Filter | null): filter is Filter {
	return !isEmptyFilter(filter);
}

export function isFilterOperator(operator: string): operator is FilterOperator {
	return FILTER_OPERATORS.includes(operator as FilterOperator);
}

export function isFilterRelationalQuantifier(key: string): key is FilterRelationalQuantifier {
	return FILTER_RELATIONAL_QUANTIFIERS.includes(key as FilterRelationalQuantifier);
}

export function isFieldFilterOperator(obj: FieldFilter | FieldFilterOperator): obj is FieldFilterOperator {
	return Object.keys(obj).some((key) => isFilterOperator(key));
}

/**
 * Extracts primitive (non-relational) field names referenced in a filter tree.
 * Walks logical operators (_and, _or, _not) recursively and collects
 * field keys from field filters whose values are direct operator expressions.
 */
export function extractPrimitiveFilterFieldNames(filter?: Filter | null): string[] {
	const names = new Set<string>();
	collectPrimitiveFilterFieldNames(filter, names);
	return [...names];
}

function collectPrimitiveFilterFieldNames(filter: Filter | null | undefined, names: Set<string>) {
	if (!filter || typeof filter !== 'object') return;

	if (isLogicalFilterAnd(filter)) {
		for (const f of filter._and) {
			collectPrimitiveFilterFieldNames(f, names);
		}
	}
	else if (isLogicalFilterOr(filter)) {
		for (const f of filter._or) {
			collectPrimitiveFilterFieldNames(f, names);
		}
	}
	else if (isUnaryFilterNot(filter)) {
		collectPrimitiveFilterFieldNames(filter._not, names);
	}
	else if (isFieldFilter(filter)) {
		for (const key of Object.keys(filter)) {
			const value = filter[key];

			if (!value || typeof value !== 'object') {
				// Implicit equality (e.g. { status: 'active' }) - primitive
				names.add(key);
				continue;
			}

			const keys = Object.keys(value);

			if (keys.some((k) => isFilterRelationalQuantifier(k))) {
				continue; // O2M quantifier (_some, _every, _none)
			}

			if (isFieldFilterOperator(value as FieldFilter)) {
				names.add(key); // Direct operator expression - primitive
			}

			// Otherwise it's an M2O traversal (nested field object) - skip
		}
	}
}

/**
 * Checks if a filter contains any relational traversal (M2O or O2M).
 *
 * Relational filters are detected by:
 * - O2M: Contains a quantifier (_some, _every, _none)
 * - M2O: Field nesting depth > 1 before hitting an operator
 *
 * Non-relational filters have the operator directly after the field name,
 * e.g., `{ status: { _eq: 'active' } }` or `{ jsonField: { _eq: { nested: 'value' } } }`
 */
export function isRelationalFilter(filter?: Filter | null): boolean {
	if (!filter) return false;

	if (isLogicalFilterAnd(filter)) {
		return filter._and.some((f) => isRelationalFilter(f));
	}

	if (isLogicalFilterOr(filter)) {
		return filter._or.some((f) => isRelationalFilter(f));
	}

	if (isUnaryFilterNot(filter)) {
		return isRelationalFilter(filter._not);
	}

	// Field filter - check for relational patterns
	return containsRelationalTraversal(filter);
}

function containsRelationalTraversal(filter: FieldFilter): boolean {
	for (const fieldKey in filter) {
		const fieldValue = filter[fieldKey];

		if (!fieldValue || typeof fieldValue !== 'object') continue;

		// Check for O2M quantifiers
		const keys = Object.keys(fieldValue);

		if (keys.some((k) => isFilterRelationalQuantifier(k))) {
			return true;
		}

		// Check for M2O: if the value is not an operator, we're traversing a relation
		if (!isFieldFilterOperator(fieldValue as FieldFilter)) {
			return true;
		}
	}

	return false;
}

export function getFirstFieldPath(filter: FieldFilter) {
	const fieldPath: string[] = [];

	do {
		const key = Object.keys(filter)[0];

		if (key && !isFilterOperator(key)) {
			fieldPath.push(key);
			filter = filter[key] as FieldFilter;
		}
		else {
			return fieldPath;
		}
	} while (typeof filter === 'object');

	return [];
}

/**
 * Extracts the first filter operator from a (nested) field filter.
 * This will return unexpected results if the filter contains multiple fields.
 */
export function getFieldFilterOperator(filter: FieldFilter) {
	const fieldPath = getFirstFieldPath(filter);
	const filterOperator = get(filter, fieldPath);

	if (!filterOperator) return;
	return filterOperator;
}

export function getFieldFilterOperatorName(filter: FieldFilter): FilterOperator | undefined {
	return Object.keys(getFieldFilterOperator(filter) ?? {})[0] as FilterOperator | undefined;
}

export function getFieldFilterOperatorValue(filter: FieldFilter): any {
	const filterOperator = getFieldFilterOperator(filter);
	if (!filterOperator) return;
	const operatorName = Object.keys(filterOperator)[0] as FilterOperator;
	return filterOperator[operatorName];
}

function isOperatorValueComplete(operator: FilterOperator, value: any): boolean {
	if (operator === FilterOperator.NULL) return value === true || value === false;

	if (operator === FilterOperator.BETWEEN || operator === FilterOperator.NBETWEEN) {
		return Array.isArray(value) && value.length === 2 && value.every((entry) => entry !== undefined && entry !== null);
	}

	// An empty operand set is meaningful — `_in: []` matches nothing — so only an absent or holed array is unfinished.
	if (operator === FilterOperator.IN || operator === FilterOperator.NIN) {
		return Array.isArray(value) && value.every((entry) => entry !== undefined && entry !== null);
	}

	// single-value scalar operators: any defined value is complete (null/false/0/'' are legitimate)
	return value !== undefined;
}

/**
 * Whether a (possibly relational) field filter has a usable value for its operator.
 * Structural — arity is determined by the operator alone, no schema needed. Descends the first key at
 * each level (matching the existing single-field convention of `getFieldFilterOperator`); a multi-field
 * field-filter object only checks its first field.
 */
export function isFieldFilterComplete(filter: FieldFilter): boolean {
	let current: any = filter;

	while (current !== null && typeof current === 'object' && !Array.isArray(current)) {
		const key = Object.keys(current)[0];
		if (key === undefined) return false; // {} — empty nesting / relation-only

		if (isFilterOperator(key)) {
			return isOperatorValueComplete(key, current[key]);
		}

		// relation segment or relational quantifier (_some/_every/_none) — keep descending
		current = current[key];
	}

	// reached a primitive (implicit equality) — complete iff defined
	return current !== undefined;
}

/**
 * Removes incomplete field-filter clauses (see {@link isFieldFilterComplete}) from a filter tree,
 * returning `undefined` when nothing applicable remains. Empty logical groups collapse to `undefined`
 * so they cannot broaden an enclosing `_or`/`_not`. `isEmptyFilter` stays structural; this is a
 * separate applicability transform.
 */
export function pruneIncompleteFilter(filter: Filter): Filter | undefined {
	if (isLogicalFilter(filter)) {
		const operator = isLogicalFilterAnd(filter) ? '_and' : '_or';
		const list = (filter as Record<string, Filter[]>)[operator] ?? [];
		const pruned = list
			.map((child) => pruneIncompleteFilter(child))
			.filter((child): child is Filter => child !== undefined);

		return pruned.length === 0 ? undefined : ({ [operator]: pruned } as Filter);
	}

	if (isUnaryFilterNot(filter)) {
		const child = pruneIncompleteFilter(filter._not);
		return child === undefined ? undefined : { _not: child };
	}

	return isFieldFilterComplete(filter as FieldFilter) ? filter : undefined;
}

/**
 * Converts the editable filter model into the filter that should be applied to queries and
 * client-side matching: one-sided ranges become open comparisons, then genuinely incomplete
 * clauses are removed.
 */
export function toApplicableFilter(filter: Filter): Filter | undefined {
	return pruneIncompleteFilter(collapseOpenRanges(filter));
}

export function createFieldFilter(fieldPath: string[], operator: FilterOperator, value?: any): FieldFilter {
	return fieldPath.reduceRight((acc, field) => ({
		[field]: acc,
	}), { [operator]: value });
}

/**
 * Rewrites a one-sided `_between`/`_nbetween` (only a lower or upper bound set) into the equivalent
 * open comparison so a partially-filled range still produces a meaningful query:
 * `_between [a, ∅]` → `_gte a`, `[∅, b]` → `_lte b`; `_nbetween [a, ∅]` → `_lt a`, `[∅, b]` → `_gt b`.
 * Complete ranges, empty ranges, and every other operator pass through untouched.
 *
 * Applied at the filter→query boundary (backend + client cache match); the editing model keeps the
 * original `_between` so the operator chip still shows "between" while a bound is unset.
 */
export function collapseOpenRanges(filter: Filter): Filter {
	if (isLogicalFilterAnd(filter)) return { _and: filter._and.map((nested) => collapseOpenRanges(nested)) };
	if (isLogicalFilterOr(filter)) return { _or: filter._or.map((nested) => collapseOpenRanges(nested)) };
	if (isUnaryFilterNot(filter)) return { _not: collapseOpenRanges(filter._not) };
	return collapseFieldFilterOpenRanges(filter as FieldFilter);
}

function collapseFieldFilterOpenRanges(filter: FieldFilter): FieldFilter {
	const result: Record<string, unknown> = {};

	for (const [key, value] of Object.entries(filter)) {
		if ((key === FilterOperator.BETWEEN || key === FilterOperator.NBETWEEN) && Array.isArray(value)) {
			const collapsed = collapseRangeBounds(key, value);

			if (collapsed) {
				result[collapsed.operator] = collapsed.value;
				continue;
			}
		}

		// Operator leaves carry operands (kept as-is); field keys and relational quantifiers nest filter structure.
		result[key] = isFilterOperator(key) || !isObject(value)
			? value
			: collapseOpenRanges(value as Filter);
	}

	return result as FieldFilter;
}

function collapseRangeBounds(operator: FilterOperator, bounds: unknown[]): { operator: FilterOperator; value: unknown } | null {
	const [lower, upper] = bounds;
	const hasLower = !isNil(lower);
	const hasUpper = !isNil(upper);

	// Only rewrite when exactly one bound is set; leave complete and empty ranges alone.
	if (hasLower === hasUpper) return null;

	const negated = operator === FilterOperator.NBETWEEN;

	if (hasLower) {
		return { operator: negated ? FilterOperator.LT : FilterOperator.GTE, value: lower };
	}

	return { operator: negated ? FilterOperator.GT : FilterOperator.LTE, value: upper };
}

export function matchesFilter<T>(item: T, filter: Filter): boolean {
	if (isEmptyFilter(filter)) {
		return true;
	}
	else if (isLogicalFilterAnd(filter) && filter._and.every((f) => matchesFilter(item, f))) {
		return true;
	}
	else if (isLogicalFilterOr(filter) && filter._or.some((f) => matchesFilter(item, f))) {
		return true;
	}
	else if (isUnaryFilterNot(filter)) {
		return !matchesFilter(item, filter._not);
	}
	else if (isFieldFilter(filter)) {
		return matchesFieldFilter(item, filter);
	}

	return false;
}

export function matchesFieldFilter<T>(item: T, filter: FieldFilter): boolean {
	for (const fieldKey in filter) {
		const keyValue = filter[fieldKey];
		const itemValue = item[fieldKey as keyof T];

		if (isObject(keyValue)) {
			const operators = Object.keys(keyValue);

			for (const operator of operators) {
				if (!operator) {
					throw new Error(`Missing operator for field ${fieldKey}`);
				}

				if (isFilterOperator(operator)) {
					const filterValue = (keyValue as FieldFilterOperator)[operator];

					// stop iterating over operators if we found a non-match
					if (!matchesOperator(itemValue, filterValue, operator)) {
						return false;
					}
				}
				else {
					console.error(`Unsupported nested field filter for field ${fieldKey}`);
					return false;
				}
			}
		}
		else {
			// special handling for implicit equal
			return matchesOperator(itemValue, keyValue, FilterOperator.EQ);
		}
	}

	return true;
}

export function matchesOperator(itemValue: any, filterValue: any, operator: FilterOperator): boolean {
	switch (operator) {
		case FilterOperator.EQ:
			return matchesComparableValue(itemValue, filterValue);

		case FilterOperator.NEQ:
			return !matchesComparableValue(itemValue, filterValue);

		case FilterOperator.CONTAINS:
			return matchesStringPredicate(itemValue, filterValue, (item, operand) => item.includes(operand));

		case FilterOperator.NCONTAINS:
			return matchesStringPredicate(itemValue, filterValue, (item, operand) => !item.includes(operand));

		case FilterOperator.ICONTAINS:
			return matchesStringPredicate(itemValue, filterValue, (item, operand) => item.toLowerCase().includes(operand.toLowerCase()));

		case FilterOperator.NICONTAINS:
			return matchesStringPredicate(itemValue, filterValue, (item, operand) => !item.toLowerCase().includes(operand.toLowerCase()));

		case FilterOperator.STARTS_WITH:
			return matchesStringPredicate(itemValue, filterValue, (item, operand) => item.startsWith(operand));

		case FilterOperator.NSTARTS_WITH:
			return matchesStringPredicate(itemValue, filterValue, (item, operand) => !item.startsWith(operand));

		case FilterOperator.ENDS_WITH:
			return matchesStringPredicate(itemValue, filterValue, (item, operand) => item.endsWith(operand));

		case FilterOperator.NENDS_WITH:
			return matchesStringPredicate(itemValue, filterValue, (item, operand) => !item.endsWith(operand));

		case FilterOperator.LT: {
			const comparison = compareFilterComparable(itemValue, filterValue);
			return comparison !== null && comparison < 0;
		}

		case FilterOperator.LTE: {
			const comparison = compareFilterComparable(itemValue, filterValue);
			return comparison !== null && comparison <= 0;
		}

		case FilterOperator.GT: {
			const comparison = compareFilterComparable(itemValue, filterValue);
			return comparison !== null && comparison > 0;
		}

		case FilterOperator.GTE: {
			const comparison = compareFilterComparable(itemValue, filterValue);
			return comparison !== null && comparison >= 0;
		}

		case FilterOperator.BETWEEN:
			if (!Array.isArray(filterValue) || filterValue.length !== 2) return false;
			return isWithinRange(itemValue, filterValue[0], filterValue[1]) === true;

		case FilterOperator.NBETWEEN:
			if (!Array.isArray(filterValue) || filterValue.length !== 2) return false;
			return isWithinRange(itemValue, filterValue[0], filterValue[1]) === false;

		case FilterOperator.IN:
			if (!Array.isArray(filterValue)) return false;

			return filterValue.some((v) => matchesComparableValue(itemValue, v));

		case FilterOperator.NIN:
			if (!Array.isArray(filterValue)) return false;

			return !filterValue.some((v) => matchesComparableValue(itemValue, v));

		case FilterOperator.NULL:
			if (filterValue === true) return isNil(itemValue);
			if (filterValue === false) return !isNil(itemValue);
			return false;

		default:
			console.error(`Unknown operator ${operator}`);
			return false;
	}
}

export function filterItems<T extends AnyItem>(items: T[], filter: Filter<T>): T[] {
	if (isEmptyFilter(filter)) {
		return items;
	}

	return items.filter((item) => matchesFilter(item, filter));
}

export function findItem<T extends AnyItem>(items: T[], filter: Filter<T>): T | undefined {
	if (isEmptyFilter(filter)) {
		return undefined;
	}

	return items.find((item) => matchesFilter(item, filter));
}
